import { useState, useEffect, useMemo, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { isTaskGroup, subtaskHint } from '@harvus/shared';
import { B, radius } from '../../constants/brand';
import { fmt } from '../../utils/format';
import { api } from '../../api/client';
import { useApi } from '../../hooks/useApi';
import { Badge } from '../ui';
import { StatusBadge } from '../ui';
import { Btn, useToast, Icon, clickable } from '../ui';

export const TASK_STATUSES  = ['Not Started', 'In Progress', 'On Hold', 'In Review', 'Complete'];
// Statuses offered when creating/editing a task and as Kanban columns.
// "Complete" is excluded there — a task is completed from a task row.
export const EDITABLE_STATUSES = ['Not Started', 'In Progress', 'On Hold', 'In Review'];
// Statuses offered by the status dropdown on a task row. Choosing "Complete"
// here is THE way to complete a task from a list (there is no checkbox), and
// choosing anything else on a completed task reopens it. The stored value
// stays "Complete" — the server's status route derives IsCompleted from it.
export const ROW_STATUSES = [...EDITABLE_STATUSES, 'Complete'];
// What a status is called on screen. Only "Complete" differs from its value.
export const statusLabel = (s) => (s === 'Complete' ? 'Completed' : (s || 'Not Started'));
// How long a row completed from its dropdown stays on screen (completed
// styling) before fading out, and how long the fade-and-collapse takes.
// Callers that refetch after a status change wait for completeRefreshDelay
// first, so the row fades rather than jumps out of the list.
export const COMPLETE_HOLD_MS = 800;
export const COMPLETE_FADE_MS = 350;
export const COMPLETE_ANIM_MS = COMPLETE_HOLD_MS + COMPLETE_FADE_MS;
export const completeRefreshDelay = (status) =>
  (status === 'Complete' ? new Promise(r => setTimeout(r, COMPLETE_ANIM_MS)) : Promise.resolve());
// Workflow status → brand color. Kept on the brand palette (not raw hexes) so
// task status reads consistently everywhere it appears: the status dropdown,
// list-row pills, and kanban card borders. 'Not Started' uses textMd (not the
// lighter textLt) to keep white pill text legible. 'On Hold' uses a neutral grey
// to read as paused/inactive.
export const STATUS_COLORS  = { 'Not Started': B.textMd, 'In Progress': B.green, 'On Hold': B.grey, 'In Review': B.amber, 'Complete': B.green };

// Pill badge for task status
export const StatusPill = ({ status }) => (
  <span style={{
    display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: 11,
    fontWeight: 700, color: '#fff', background: STATUS_COLORS[status] || '#6b7280',
    whiteSpace: 'nowrap',
  }}>{statusLabel(status)}</span>
);

// Status pill that doubles as an inline dropdown — change a task's status
// straight from a table row without opening the detail modal.
//
// THE MENU IS RENDERED IN A PORTAL, FIXED-POSITIONED OFF THE PILL, NOT
// ABSOLUTELY-POSITIONED INSIDE THE ROW. Every table that hosts a TaskRow sits
// in a `<Card style={{ overflow: 'hidden' }}>` (Dashboard, Tasks) so
// the card's rounded corners clip its rows — and an absolute child of the last
// row is clipped with them: on the Dashboard the list opened and showed one
// option, the rest cut off at the card's bottom edge. A portal at
// `document.body` is outside every clipping ancestor, so it does not matter
// which container a row lands in. The position is measured from the pill's
// bounding rect when the menu opens and on scroll/resize while it is open.
export const InlineStatusSelect = ({ status, onChange, align = 'left' }) => {
  const [open, setOpen] = useState(false);
  const [at, setAt] = useState(null);
  const btnRef = useRef(null);
  const cur = status || 'Not Started';

  useLayoutEffect(() => {
    if (!open) { setAt(null); return undefined; }
    const place = () => {
      const r = btnRef.current?.getBoundingClientRect();
      if (!r) return;
      // Below the pill by default; above it when there is no room below —
      // a row at the bottom of a long page must not open off-screen.
      const menuH = ROW_STATUSES.length * 32 + 10;
      const below = window.innerHeight - r.bottom;
      const pos = below < menuH && r.top > menuH
        ? { bottom: window.innerHeight - r.top + 4 }
        : { top: r.bottom + 4 };
      if (align === 'right') pos.right = window.innerWidth - r.right;
      else pos.left = r.left;
      setAt(pos);
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, align]);

  return (
    // onClick here is event containment, not a control — it keeps a click on the
    // pill from reaching the row behind it. It must NOT be focusable. The
    // keyboard's equivalent of "click the backdrop to dismiss" is Escape, which
    // onKeyDown provides; the backdrop div below stays mouse-only by design.
    <div style={{ position: 'relative', display: 'inline-block' }}
      onClick={e => e.stopPropagation()}
      onKeyDown={e => { if (e.key === 'Escape' && open) { e.stopPropagation(); setOpen(false); } }}>
      <button ref={btnRef} onClick={() => setOpen(o => !o)} title="Change status"
        aria-haspopup="listbox" aria-expanded={open}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px',
          borderRadius: 12, fontSize: 11, fontWeight: 700, color: '#fff',
          background: STATUS_COLORS[cur] || '#6b7280', border: 'none', cursor: 'pointer',
          fontFamily: 'inherit', whiteSpace: 'nowrap',
        }}>
        {statusLabel(cur)} <Icon name="chevronDown" size={11} style={{ display: 'inline-block', verticalAlign: 'middle' }} />
      </button>
      {open && at && createPortal(
        // The portal content keeps the same containment: a click inside it must
        // not reach whatever sits under the menu in the DOM (document.body),
        // and Escape closes it from the keyboard.
        <div onClick={e => e.stopPropagation()}
          onKeyDown={e => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); } }}>
          <div aria-hidden="true" onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 200 }} />
          <div role="listbox" aria-label="Task status" style={{
            position: 'fixed', ...at, zIndex: 201,
            background: B.white, border: `1px solid ${B.border}`, borderRadius: 8, boxShadow: B.shadowMd,
            overflow: 'hidden', minWidth: 130,
          }}>
            {ROW_STATUSES.map(s => (
              <div key={s} {...clickable(() => { setOpen(false); if (s !== cur) onChange(s); }, { role: 'option' })}
                aria-selected={s === cur}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', cursor: 'pointer',
                  fontSize: 12, fontWeight: 600, color: B.text, background: s === cur ? B.surfaceMuted : B.white, whiteSpace: 'nowrap' }}
                onMouseEnter={e => e.currentTarget.style.background = B.surfaceMuted}
                onMouseLeave={e => e.currentTarget.style.background = s === cur ? B.surfaceMuted : B.white}>
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: STATUS_COLORS[s], flexShrink: 0 }} />
                {statusLabel(s)}
              </div>
            ))}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};

// Initials avatar
export const Avatar = ({ name, size = 24 }) => (
  <div title={name} style={{
    width: size, height: size, borderRadius: '50%', background: B.tealDk,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: size * 0.38, fontWeight: 700, color: '#fff', flexShrink: 0,
  }}>{fmt.initials(name)}</div>
);

// Collapsible group header for list view. `lead` (optional) shows the deal's
// Harvus lead(s) next to the group label when grouping tasks by deal.
export const GroupHeader = ({ label, count, collapsed, onToggle, color, lead }) => (
  <div {...clickable(onToggle)} style={{
    display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px',
    background: color || B.surfaceMuted, borderRadius: 8, cursor: 'pointer',
    marginBottom: collapsed ? 8 : 4, userSelect: 'none',
    borderLeft: `4px solid ${color || B.navy}`,
  }}>
    <span style={{ color: B.textMd, transition: 'transform 0.15s', display: 'inline-flex', transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}><Icon name="chevronDown" size={14} /></span>
    <span style={{ fontWeight: 700, fontSize: 13, color: B.text }}>{label}</span>
    {lead && (
      <span title={`Harvus lead: ${lead}`} style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        fontSize: 11, fontWeight: 600, color: B.tealDk, background: B.white,
        borderRadius: 10, padding: '1px 8px', border: `1px solid ${B.border}`, whiteSpace: 'nowrap',
      }}><Icon name="user" size={11} /> {lead}</span>
    )}
    <span style={{ flex: 1 }} />
    <span style={{ fontSize: 11, color: B.textLt, background: B.white, borderRadius: 10, padding: '1px 8px', border: `1px solid ${B.border}` }}>{count}</span>
  </div>
);

// A PARENT TASK IS A GROUP HEADING, NOT A TASK (shared/src/taskTree.ts). The
// moment a task has a live subtask it renders as the heading its subtasks sit
// under: chevron + title + how many are open, with no checkbox, no status, no
// priority, no due date and no assignees of its own — the work is the leaves.
// Clicking still opens it (to rename it or add subtasks) and the hover actions
// keep Add subtask / Add note.
const TaskGroupRow = ({ task, depth, expanded, onToggle, onOpen, onAddChild, onQuickNote, shownChildCount }) => {
  const [hovered, setHovered] = useState(false);
  const hint = subtaskHint(task);
  return (
    <div {...clickable(() => onOpen(task))}
      aria-label={`Open task group: ${task.title}`}
      data-task-group="true"
      style={{
        display: 'flex', alignItems: 'center', gap: 0,
        paddingLeft: 12 + depth * 24, paddingRight: 12,
        paddingTop: 9, paddingBottom: 9,
        borderBottom: `1px solid ${B.border}`,
        background: B.surfaceMuted,
        cursor: 'pointer',
        transition: 'background 0.1s',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = B.border; setHovered(true); }}
      onMouseLeave={e => { e.currentTarget.style.background = B.surfaceMuted; setHovered(false); }}
    >
      <div style={{ width: 20, flexShrink: 0 }}>
        {shownChildCount > 0 && (
          <span onClick={e => { e.stopPropagation(); onToggle(task.taskID); }}
            style={{ color: B.textMd, cursor: 'pointer', display: 'inline-flex', transition: 'transform 0.15s', transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)' }}>
            <Icon name="chevronDown" size={13} />
          </span>
        )}
      </div>
      {/* A group shows a folder before its title — the one glyph that says
          "this holds things" rather than "this is a thing to do". */}
      <div style={{ width: 16, height: 16, flexShrink: 0, marginRight: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: B.textMd }}>
        <Icon name={expanded ? 'folderOpen' : 'folder'} size={14} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 700, fontSize: 13, color: B.text }}>{task.title}</span>
          {(task.unreadMessageCount || 0) > 0 && <Badge color={B.navy}>{task.unreadMessageCount}</Badge>}
        </div>
        {hint && <div style={{ fontSize: 11, color: B.textLt }}>{hint}</div>}
      </div>
      <div style={{ width: 74, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4, paddingRight: 4 }}>
        {onQuickNote && (
          <button onClick={e => { e.stopPropagation(); onQuickNote(task); }} title="Add note"
            style={{ opacity: hovered ? 1 : 0, transition: 'opacity 0.15s', width: 20, height: 20, borderRadius: 4, border: `1px solid ${B.border}`, background: B.white, color: B.textMd, lineHeight: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'inherit', padding: 0 }}>
            <Icon name="note" size={12} /></button>
        )}
        {depth === 0 && onAddChild && (
          <button onClick={e => { e.stopPropagation(); onAddChild(task); }} title="Add subtask"
            style={{ opacity: hovered ? 1 : 0, transition: 'opacity 0.15s', width: 20, height: 20, borderRadius: 4, border: `1px solid ${B.border}`, background: B.white, color: B.textMd, lineHeight: 1, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'inherit', padding: 0 }}>
            <Icon name="add" size={13} /></button>
        )}
      </div>
      {/* The four task columns stay as spacers so the heading lines up under
          the column header; a group has nothing to put in them. */}
      <div style={{ width: 100, flexShrink: 0, textAlign: 'center', fontSize: 11, color: B.textLt, letterSpacing: 0.5, textTransform: 'uppercase' }}>Group</div>
      <div style={{ width: 80, flexShrink: 0 }} />
      <div style={{ width: 90, flexShrink: 0 }} />
      <div style={{ width: 80, flexShrink: 0, paddingLeft: 8 }} />
    </div>
  );
};

// Single task row in list view
//
// THERE IS NO CHECKBOX. A task is completed by choosing "Completed" from the
// row's status dropdown — a deliberate action rather than a stray tick. The
// row then shows as completed for a moment and, where completed tasks are
// hidden (`fadeOnComplete`, the default), fades and collapses out while the
// caller waits for completeRefreshDelay before refetching. onStatusChange may
// return false to say the change failed, which puts the row back.
export const TaskRow = ({ task, depth = 0, expanded, onToggle, onOpen, onAddChild, onStatusChange, onQuickNote, showDeal, showParent, visibleChildCount, fadeOnComplete = true }) => {
  const [hovered, setHovered] = useState(false);
  // 'completing' = just set to Complete from this row, shown as completed;
  // 'fading' = fading out. Local only, and cleared by a fresh server status.
  const [phase, setPhase] = useState(null);
  const timerRef = useRef(null);
  useEffect(() => () => clearTimeout(timerRef.current), []);
  useEffect(() => { clearTimeout(timerRef.current); setPhase(null); }, [task.status, task.isCompleted]);
  const completed = !!task.isCompleted || phase != null;
  const overdue = task.dueDate && !completed && new Date(task.dueDate) < new Date();
  const changeStatus = async (s) => {
    if (s === 'Complete' && !task.isCompleted) {
      setPhase('completing');
      if (fadeOnComplete) {
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => setPhase('fading'), COMPLETE_HOLD_MS);
      }
    }
    let ok;
    try { ok = await onStatusChange(task, s); } catch { ok = false; }
    if (ok === false) { clearTimeout(timerRef.current); setPhase(null); }
  };
  // The chevron + "N subtasks" label reflect what expanding actually reveals.
  // Callers that hold the full child list in memory pass visibleChildCount (the
  // subtasks that survive the current show-completed / search filters); without
  // it we fall back to the backend's total ChildCount (e.g. the dashboard, which
  // lazy-loads children on expand and needs the chevron shown up front). This is
  // what stops an all-subtasks-complete parent from showing a chevron + count
  // that expand to nothing.
  const shownChildCount = visibleChildCount != null ? visibleChildCount : (task.childCount || 0);
  if (isTaskGroup(task)) {
    return <TaskGroupRow task={task} depth={depth} expanded={expanded} onToggle={onToggle} onOpen={onOpen}
      onAddChild={onAddChild} onQuickNote={onQuickNote} shownChildCount={shownChildCount} />;
  }
  return (
    // ONE TAB STOP PER ROW, NOT ONE PER CELL. Five cells used to carry their own
    // onClick={() => onOpen(task)}; as keyboard controls that would be five stops
    // that all do the same thing. The handler lives on the row instead, so the
    // whole row is the control it already looked like (it has always had
    // cursor:pointer) and the gaps between cells are clickable too. Every inner
    // control — chevron, status select, note/subtask buttons — calls
    // stopPropagation, so they keep their own behaviour.
    <div {...clickable(() => onOpen(task))}
    aria-label={`Open task: ${task.title}`}
    data-completing={phase || undefined}
    style={{
      display: 'flex', alignItems: 'center', gap: 0,
      paddingLeft: 12 + depth * 24, paddingRight: 12,
      paddingTop: phase === 'fading' ? 0 : 9, paddingBottom: phase === 'fading' ? 0 : 9,
      borderBottom: `1px solid ${B.border}`,
      background: completed ? '#fafaf9' : '#fff',
      cursor: 'pointer',
      overflow: phase ? 'hidden' : undefined,
      maxHeight: phase === 'fading' ? 0 : (phase ? 200 : undefined),
      opacity: phase === 'fading' ? 0 : 1,
      transition: `background 0.1s, opacity ${COMPLETE_FADE_MS}ms ease, max-height ${COMPLETE_FADE_MS}ms ease, padding ${COMPLETE_FADE_MS}ms ease`,
    }}
    onMouseEnter={e => { e.currentTarget.style.background = B.surfaceMuted; setHovered(true); }}
    onMouseLeave={e => { e.currentTarget.style.background = completed ? '#fafaf9' : '#fff'; setHovered(false); }}
    >
      {/* Expand/collapse chevron for parents */}
      <div style={{ width: 20, flexShrink: 0 }}>
        {shownChildCount > 0 && (
          <span onClick={e => { e.stopPropagation(); onToggle(task.taskID); }}
            style={{ color: B.textMd, cursor: 'pointer', display: 'inline-flex', transition: 'transform 0.15s', transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)' }}>
            <Icon name="chevronDown" size={13} />
          </span>
        )}
      </div>

      {/* Title */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{
            fontWeight: 600, fontSize: 13, color: completed ? B.textLt : B.text,
            textDecoration: completed ? 'line-through' : 'none',
          }}>{task.title}</span>
          {showDeal && task.dealTitle && (
            <span style={{ fontSize: 11, fontWeight: 400, color: B.textLt }}>
              {task.dealDetailTitle ? `${task.dealTitle} (${task.dealDetailTitle})` : task.dealTitle}
            </span>
          )}
          {/* Project is an ORTHOGONAL TAG, never implied by scope the way a
              deal name is — shown unconditionally when the task carries one,
              unlike showDeal's redundant-on-a-deal-scoped-list gate. */}
          {task.projectTitle && <Badge color={B.tealDk}>{task.projectTitle}</Badge>}
          {!!task.isBlocked && !completed && <Badge color={B.amber}>Blocked</Badge>}
          {(task.unreadMessageCount || 0) > 0 && <Badge color={B.navy}>{task.unreadMessageCount}</Badge>}
        </div>
        {shownChildCount > 0 && !expanded &&
          <div style={{ fontSize: 11, color: B.textLt }}>{shownChildCount} subtask{shownChildCount > 1 ? 's' : ''}</div>}
        {/* Parent task — surfaced on flat lists (e.g. the dashboard) where a
            subtask appears out of its tree, so its parent is still visible.
            Only at depth 0; nested rows already sit under their parent. */}
        {showParent && depth === 0 && task.parentTaskTitle && (
          <div title={`Subtask of "${task.parentTaskTitle}"`} style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 11, color: B.textLt, minWidth: 0 }}>
            <span aria-hidden="true" style={{ flexShrink: 0 }}>↳</span>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{task.parentTaskTitle}</span>
          </div>
        )}
        {/* Surface co-assignors only when a task was assigned BY more than one
            person — the common single-assignor case is implied and stays quiet. */}
        {(task.assignorIDs || '').split(',').filter(Boolean).length > 1 && (
          <div style={{ fontSize: 11, color: B.textLt }}>Assigned by {task.assignorNames}</div>
        )}
      </div>

      {/* Quick-note + add-subtask actions — visible on hover */}
      <div style={{ width: 74, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4, paddingRight: 4 }}>
        {onQuickNote && (
          <button
            onClick={e => { e.stopPropagation(); onQuickNote(task); }}
            title="Add note"
            style={{
              opacity: hovered ? 1 : 0, transition: 'opacity 0.15s',
              width: 20, height: 20, borderRadius: 4, border: `1px solid ${B.border}`,
              background: B.white, color: B.textMd, lineHeight: 1,
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: 'inherit', padding: 0,
            }}><Icon name="note" size={12} /></button>
        )}
        {depth === 0 && onAddChild && (
          <button
            onClick={e => { e.stopPropagation(); onAddChild(task); }}
            title="Add subtask"
            style={{
              opacity: hovered ? 1 : 0, transition: 'opacity 0.15s',
              width: 20, height: 20, borderRadius: 4, border: `1px solid ${B.border}`,
              background: B.white, color: B.textMd, lineHeight: 1,
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: 'inherit', padding: 0,
            }}><Icon name="add" size={13} /></button>
        )}
      </div>

      {/* Status */}
      <div style={{ width: 100, flexShrink: 0, textAlign: 'center', display: 'flex', justifyContent: 'center' }}>
        {onStatusChange
          ? <InlineStatusSelect status={phase ? 'Complete' : task.status} onChange={changeStatus} />
          : <StatusPill status={phase ? 'Complete' : task.status} />}
      </div>

      {/* Priority */}
      <div style={{ width: 80, flexShrink: 0, textAlign: 'center' }}>
        <StatusBadge status={task.priority} />
      </div>

      {/* Due date */}
      <div style={{ width: 90, flexShrink: 0, fontSize: 12, color: overdue ? B.red : B.textMd, textAlign: 'right' }}>
        {fmt.date(task.dueDate)}
      </div>

      {/* Assignees */}
      <div style={{ width: 80, flexShrink: 0, display: 'flex', gap: 4, justifyContent: 'flex-end', paddingLeft: 8 }}>
        {(task.assigneeNames || '').split(', ').filter(Boolean).slice(0, 3).map(n => (
          <Avatar key={n} name={n} size={22} />
        ))}
      </div>
    </div>
  );
};

// Inline one-line "quick note" composer shown under a task row when its
// note affordance is tapped. Owns its own text/saving state and the note POST,
// so the three task-list surfaces (Dashboard, Tasks page, DealTasksPanel) share
// one implementation instead of each copy-pasting the input + save logic.
// Callers keep only "which row is composing" state and render this in place,
// passing onClose to clear it.
export const QuickNoteComposer = ({ taskId, taskTitle, onClose, onSaved }) => {
  const { toast } = useToast();
  const [text, setText]     = useState('');
  const [saving, setSaving] = useState(false);

  // Wrap the plain quick-note line in minimal BlockNote JSON so it renders as a
  // normal note (and is safe — no raw HTML injection) in the notes feed.
  const contentJson = (t) =>
    JSON.stringify([{ type: 'paragraph', content: t ? [{ type: 'text', text: t, styles: {} }] : [] }]);

  // iOS Safari freeze fix: saving/cancelling unmounts this input. Tearing down a
  // focused input while the soft keyboard is open leaves the page stuck (viewport
  // offset, taps/scrolls ignored). Blur first — while still inside the tap
  // gesture, so iOS honors it — to dismiss the keyboard before the unmount.
  const blurActive = () => { if (typeof document !== 'undefined') document.activeElement?.blur?.(); };

  const save = async () => {
    const trimmed = text.trim();
    blurActive();
    if (!trimmed) { onClose(); return; }
    setSaving(true);
    try {
      await api.post('notes', { taskId, content: contentJson(trimmed), mentionedUserIDs: [], attachments: [] });
      onClose();
      toast.success('Note added');
      onSaved?.();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const cancel = () => { blurActive(); onClose(); };

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '8px 12px 8px 46px', background: B.surfaceMuted, borderBottom: `1px solid ${B.border}` }}>
      <input autoFocus value={text} onChange={e => setText(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); save(); } if (e.key === 'Escape') cancel(); }}
        placeholder={`Add a note to "${taskTitle}"…`}
        style={{ flex: 1, fontSize: 13, padding: '6px 10px', border: `1px solid ${B.border}`, borderRadius: 6, fontFamily: 'inherit', outline: 'none' }} />
      <Btn size="sm" disabled={!text.trim() || saving} onClick={save}>{saving ? 'Saving…' : 'Add note'}</Btn>
      <Btn size="sm" variant="ghost" onClick={cancel}>Cancel</Btn>
    </div>
  );
};

// Kanban card
export const KanbanCard = ({ task, onOpen, onDragStart }) => {
  const overdue = task.dueDate && !task.isCompleted && new Date(task.dueDate) < new Date();
  return (
    <div
      draggable
      onDragStart={e => onDragStart(e, task)}
      {...clickable(() => onOpen(task))}
      aria-label={`Open task: ${task.title}`}
      style={{
        background: B.surface, borderRadius: radius.xxl, padding: '11px 13px', marginBottom: 8,
        boxShadow: B.shadowXs, cursor: 'grab', border: `1px solid ${B.border}`,
        borderLeft: `3px solid ${STATUS_COLORS[task.status] || B.border}`,
        transition: 'box-shadow 0.18s ease, transform 0.18s ease',
      }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = B.shadowMd; e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = B.shadowXs; e.currentTarget.style.transform = 'translateY(0)'; }}
    >
      <div style={{ fontWeight: 600, fontSize: 13, color: B.text, marginBottom: 6, lineHeight: 1.35 }}>{task.title}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: task.assigneeNames ? 8 : 0 }}>
        <StatusBadge status={task.priority} />
        {task.isBlocked && <Badge color={B.amber}>Blocked</Badge>}
        {task.projectTitle && <Badge color={B.tealDk}>{task.projectTitle}</Badge>}
        {task.dealTitle && <span style={{ fontSize: 10, color: B.textLt, background: B.surfaceMuted, borderRadius: 4, padding: '1px 6px' }}>{task.dealTitle}</span>}
      </div>
      {/* Co-assignors — only when assigned BY more than one person. */}
      {(task.assignorIDs || '').split(',').filter(Boolean).length > 1 && (
        <div style={{ fontSize: 10, color: B.textLt, marginBottom: 8 }}>Assigned by {task.assignorNames}</div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: 3 }}>
          {(task.assigneeNames || '').split(', ').filter(Boolean).slice(0, 3).map(n => (
            <Avatar key={n} name={n} size={20} />
          ))}
        </div>
        {task.dueDate && (
          <span style={{ fontSize: 11, color: overdue ? B.red : B.textLt, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
            {overdue && <Icon name="warning" size={11} />}{fmt.date(task.dueDate)}
          </span>
        )}
      </div>
      {(task.unreadMessageCount || 0) > 0 && (
        <div style={{ marginTop: 6 }}>
          <Badge color={B.navy}>{task.unreadMessageCount} unread</Badge>
        </div>
      )}
    </div>
  );
};

// Generic single-item search picker — used for the deal/detail and parent-task pickers
// `match(item, query)` replaces the default label substring test (and the
// deal/detail grouping) — e.g. word-by-word matching for a long flat list.
export const InlineSearchPicker = ({ label, items, value, onChange, placeholder, clearLabel, match }) => {
  const selectedItem = (items || []).find(i => i.value === value);
  const [query, setQuery]   = useState(selectedItem?.label || '');
  const [open,  setOpen]    = useState(false);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!value) return; // Don't clear — pick(null) handles clearing on explicit deselect
    const match = (items || []).find(i => i.value === value);
    if (match) setQuery(match.label);
  }, [value, items]); // eslint-disable-line react-hooks/exhaustive-deps

  // Filter on label, but keep deal/detail groups intact: if a parent deal
  // matches, its child details come along, and if a child detail matches, its
  // parent deal is shown too (grouped by `dealID`). Items without a `dealID`
  // (e.g. the parent-task picker) fall back to a plain label match.
  const q = query.trim().toLowerCase();
  const all = items || [];
  const matchedDealIDs = new Set(
    all.filter(i => i.dealID != null && i.label.toLowerCase().includes(q))
       .map(i => i.dealID)
  );
  const filtered = match ? all.filter(i => !q || match(i, query)) : all.filter(i =>
    !q ||
    i.label.toLowerCase().includes(q) ||
    (i.dealID != null && matchedDealIDs.has(i.dealID))
  );

  const pick = (item) => {
    onChange(item ? item.value : '');
    setQuery(item ? item.label : '');
    setOpen(false);
  };

  return (
    <div style={{ position: 'relative', marginBottom: 16 }}>
      {label && <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: B.textMd, marginBottom: 4, letterSpacing: 0.5 }}>{label}</label>}
      <div style={{ position: 'relative' }}>
        <input
          type="text"
          value={query}
          placeholder={placeholder || 'Search…'}
          onFocus={() => { setFocused(true); setOpen(true); }}
          onBlur={() => setTimeout(() => { setFocused(false); setOpen(false); }, 150)}
          onChange={e => { setQuery(e.target.value); setOpen(true); onChange(''); }}
          style={{
            width: '100%', boxSizing: 'border-box',
            border: `1px solid ${focused ? B.navy : B.border}`, borderRadius: 6,
            padding: '9px 32px 9px 12px', fontSize: 13,
            fontFamily: 'inherit', outline: 'none',
            background: value ? B.surfaceMuted : B.white, color: B.text,
            transition: 'border-color 0.15s',
          }}
        />
        {value && (
          <button onClick={() => pick(null)} aria-label="Clear" style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: B.textMd, lineHeight: 1, display: 'flex' }}><Icon name="close" size={14} /></button>
        )}
      </div>
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 300,
          background: B.white, border: `1px solid ${B.border}`, borderRadius: 8,
          boxShadow: B.shadowMd, maxHeight: 200, overflowY: 'auto', marginTop: 2,
        }}>
          <div {...clickable(() => pick(null), { role: 'option' })}
            style={{ padding: '8px 12px', fontSize: 12, color: B.textLt, cursor: 'pointer', borderBottom: `1px solid ${B.border}`, fontStyle: 'italic' }}
            onMouseEnter={e => e.currentTarget.style.background = B.surfaceMuted}
            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            {clearLabel || '— None —'}
          </div>
          {filtered.length === 0 && <div style={{ padding: '8px 12px', fontSize: 12, color: B.textLt }}>No matches</div>}
          {filtered.map(item => (
            <div key={item.value} {...clickable(() => pick(item), { role: 'option' })}
              style={{ padding: `10px 14px 10px ${item.indent === 2 ? 42 : item.indent ? 28 : 14}px`, cursor: 'pointer', borderBottom: `1px solid ${B.border}`, background: value === item.value ? B.tealLt : 'transparent' }}
              onMouseEnter={e => { if (value !== item.value) e.currentTarget.style.background = B.surfaceMuted; }}
              onMouseLeave={e => { if (value !== item.value) e.currentTarget.style.background = 'transparent'; }}>
              <div style={{ fontSize: 13, fontWeight: value === item.value ? 600 : 400, color: item.indent ? B.textMd : B.text }}>
                {item.indent ? `· ${item.label}` : item.label}
              </div>
              {item.subtitle && <div style={{ fontSize: 11, color: B.textLt }}>{item.subtitle}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Detail type → human label for the picker rows. A detail's own Description
// frequently just repeats the deal name (createDealSvc seeds the first detail
// that way), so the type is what actually tells two rows apart.
const DETAIL_TYPE_LABEL = {
  'R/E': 'Real Estate', 'N/R': 'Loan', 'Active': 'Investment', 'Passive': 'Investment',
};

// Label for one deal-detail row, under its parent deal's row.
function dealDetailLabel(detail, dealName) {
  const typeLabel = DETAIL_TYPE_LABEL[detail.detailType] || detail.detailType || 'Detail';
  const desc      = (detail.description || '').trim();
  // A description that only echoes the deal name adds nothing here — the row is
  // already indented under it — so fall back to the capital type.
  return desc && desc.toLowerCase() !== (dealName || '').trim().toLowerCase()
    ? `${desc} — ${typeLabel}`
    : typeLabel;
}

// Shared deal + detail picker data for every "which deal (or deal detail) does
// this belong to?" search picker — the task editor, the New Task dialog, the
// email inbox, Global Notes, the document-migration mapper. Keeping the fetch +
// item shape here means every consumer stays in sync: update once, not in five
// places.
//
// EVERY live deal detail is offered, including the lone detail of a
// single-detail deal. This used to be gated on the deal exposing more than one
// detail, which — since every deal has at least one — made those details
// unselectable, so a task/note/document could only land in the deal-level
// bucket. One `deal-detail-options` request now replaces the old fan-out of one
// `deals/{id}/details` call per multi-detail deal.
//
// Returns:
//   dealPickerOptions — deals ({ dealID, name, status, detailCount, details })
//   dealPickerItems   — InlineSearchPicker items: one `deal:<id>` per deal, plus an
//                       indented `detail:<id>` per detail; each item carries
//                       { dealID, dealDetailID } so callers can resolve a selection.
//   dealPickerError   — the fetch error, if any; without it a caller can't tell an
//                       empty picker from a failed load.
//
// PROJECTS ARE OPT-IN (`includeProjects`). A project is a tag on a task or note,
// chosen in the SAME list as its deal/detail rather than in a second box: a
// `project:<id>` row sits under its deal (or under its detail when the project
// is scoped to one), and picking it resolves the deal, the detail and the
// project in one step. Pickers that file emails, documents or chat leave it off
// — a project is not a place to put a file. Every item carries `projectID`
// ('' when it is not a project row).
//
// `onlyDealId` narrows the list to one deal — the deal's own panels, where the
// deal is fixed and only its details and projects are a choice.
export function buildDealPickerItems(deals, { includeProjects = false, onlyDealId = null } = {}) {
  const items = [];
  const norm = v => String(v || '').toLowerCase();
  const only = onlyDealId ? norm(onlyDealId) : null;
  for (const deal of (deals || [])) {
    if (only && norm(deal.dealID) !== only) continue;
    items.push({ value: `deal:${deal.dealID}`, label: deal.name, dealID: deal.dealID, dealDetailID: '', projectID: '' });
    const projects = includeProjects ? (deal.projects || []) : [];
    const projectRow = (p, indent) => ({
      value:        `project:${p.projectID}`,
      label:        `📁 ${p.name}`,
      subtitle:     `Project · ${deal.name}${p.status && p.status !== 'Active' ? ` · ${p.status}` : ''}`,
      dealID:       deal.dealID,
      dealDetailID: p.dealDetailID || '',
      projectID:    p.projectID,
      indent,
    });
    const detailIds = new Set((deal.details || []).map(d => norm(d.dealDetailID)));
    for (const d of (deal.details || [])) {
      items.push({
        value:        `detail:${d.dealDetailID}`,
        label:        dealDetailLabel(d, deal.name),
        subtitle:     d.sageLocationName || null,
        dealID:       deal.dealID,
        dealDetailID: d.dealDetailID,
        projectID:    '',
        indent:       true,
      });
      for (const p of projects) {
        if (p.dealDetailID && norm(p.dealDetailID) === norm(d.dealDetailID)) items.push(projectRow(p, 2));
      }
    }
    // Deal-level projects (and any whose detail is no longer live) sit under the deal.
    for (const p of projects) {
      if (!p.dealDetailID || !detailIds.has(norm(p.dealDetailID))) items.push(projectRow(p, true));
    }
  }
  return items;
}

// The picker value for a stored (dealId, dealDetailId, projectId) triple — the
// most specific of the three, so a project-tagged item opens on its project.
export function dealPickerValueFor({ dealId, dealDetailId, projectId } = {}) {
  if (projectId) return `project:${projectId}`;
  if (dealDetailId) return `detail:${dealDetailId}`;
  if (dealId) return `deal:${dealId}`;
  return '';
}

export function useDealPickerItems({ includeProjects = false, onlyDealId = null } = {}) {
  const { data, error } = useApi(() => api.get('deal-detail-options'), []);

  const dealPickerOptions = useMemo(() => (data?.deals || []), [data]);

  const dealPickerItems = useMemo(
    () => buildDealPickerItems(dealPickerOptions, { includeProjects, onlyDealId }),
    [dealPickerOptions, includeProjects, onlyDealId],
  );

  return { dealPickerOptions, dealPickerItems, dealPickerError: error };
}

// InlineSearchPicker items for open tasks, so a captured email/thread can be
// attached to a task's notes (not just a deal). One `task:<id>` per task; the
// deal name (when present) is shown as a subtitle for disambiguation. Complete
// tasks are excluded — you don't file new correspondence against closed work.
export function useTaskPickerItems() {
  const { data: tasks } = useApi(() => api.get('tasks'), []);
  const taskPickerItems = (tasks || [])
    .filter(t => t.status !== 'Complete')
    .map(t => ({
      value:    `task:${t.taskID}`,
      label:    t.title || '(untitled task)',
      taskId:   t.taskID,
      subtitle: t.dealName || null,
    }));
  return { taskPickerItems };
}
